import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "lib/utils";

const buttonVariants = cva(
  "!cursor-pointer !inline-flex !items-center !justify-center ![gap:8px] !whitespace-nowrap ![border-radius:8px] !text-sm !font-semibold !transition-all disabled:!pointer-events-none disabled:!opacity-50 [&_svg]:!pointer-events-none [&_svg:not([class*='size-'])]:!size-4 !shrink-0 [&_svg]:!shrink-0 [&>i]:!shrink-0 !outline-none focus-visible:!border-ring focus-visible:!ring-ring/50 focus-visible:!ring-[3px] aria-invalid:!ring-destructive/20 dark:aria-invalid:!ring-destructive/40 aria-invalid:!border-destructive ![font-family:var(--font-inter)]",
  {
    variants: {
      variant: {
        default:
          "!bg-blue-500 !text-white !shadow-xs hover:!bg-blue-600 active:!bg-blue-700 disabled:!bg-neutral-200 disabled:!text-white",
        "blue-primary":
          "!bg-blue-500 !text-white !shadow-xs hover:!bg-blue-600 active:!bg-blue-700 disabled:!bg-neutral-200 disabled:!text-white",
        "blue-secondary":
          "!border !border-blue-500 !text-blue-500 !bg-white !shadow-xs hover:!bg-blue-25 active:!bg-blue-50 disabled:!border-neutral-200 disabled:!text-neutral-200 disabled:!bg-white",
        "blue-tertiary":
          "!text-blue-500 !bg-white hover:!bg-blue-25 active:!bg-blue-50 disabled:!text-neutral-200 disabled:!bg-white",
        "green-primary":
          "!bg-green-500 !text-white !shadow-xs hover:!bg-green-600 active:!bg-green-700 disabled:!bg-neutral-200 disabled:!text-white",
        "green-secondary":
          "!border !border-green-500 !text-green-500 !bg-white !shadow-xs hover:!bg-green-25 active:!bg-green-50 disabled:!border-neutral-200 disabled:!text-neutral-200 disabled:!bg-white",
        "green-tertiary":
          "!text-green-500 !bg-white hover:!bg-green-25 active:!bg-green-50 disabled:!text-neutral-200 disabled:!bg-white",
        "red-primary":
          "!bg-red-500 !text-white !shadow-xs hover:!bg-red-600 active:!bg-red-700 disabled:!bg-neutral-200 disabled:!text-white focus-visible:!ring-red-500/20 dark:focus-visible:!ring-red-500/40",
        "red-secondary":
          "!border !border-red-500 !text-red-500 !bg-white !shadow-xs hover:!bg-red-25 active:!bg-red-50 disabled:!border-neutral-200 disabled:!text-neutral-200 disabled:!bg-white focus-visible:!ring-red-500/20 dark:focus-visible:!ring-red-500/40",
        "red-tertiary":
          "!text-red-500 !bg-white hover:!bg-red-25 active:!bg-red-50 disabled:!text-neutral-200 disabled:!bg-white focus-visible:!ring-red-500/20 dark:focus-visible:!ring-red-500/40",
        "orange-primary":
          "!bg-[var(--color-warning-500)] !text-white !shadow-xs hover:!bg-[var(--color-warning-600)] active:!bg-[var(--color-warning-700)] disabled:!bg-neutral-200 disabled:!text-white",
        "black-secondary":
          "!border !border-neutral-800 !text-neutral-800 !bg-white !shadow-xs hover:!bg-blue-25 active:!bg-blue-50 disabled:!border-neutral-200 disabled:!text-neutral-200 disabled:!bg-white",
        link: "!text-blue-500 !underline-offset-4 hover:!underline disabled:!text-blue-300",
      },
      //add font size
      size: {
        xxs: "!h-[24px] ![padding:4px_8px] ![font-size:12px] [&>i]:![font-size:14px]",
        xs: "!h-[32px] ![padding:8px_26px] ![font-size:12px] [&>i]:![font-size:14px]",
        sm: "!h-[36px] ![padding:8px_28px] ![font-size:14px] [&>i]:![font-size:16px]",
        md: "!h-[40px] ![padding:10px_30px] ![font-size:14px] [&>i]:![font-size:16px]",
        lg: "!h-[44px] ![padding:10px_32px] ![font-size:16px] [&>i]:![font-size:18px]",
        xl: "!h-[48px] ![padding:12px_34px] ![font-size:16px] [&>i]:![font-size:18px]",
        "2xl":
          "!h-[60px] ![padding:16px_46px] ![font-size:18px] [&>i]:![font-size:20px]",
        icon: "!size-[36px] [&>i]:![font-size:16px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

type ButtonProps = React.ComponentPropsWithoutRef<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";
